/**
 * @fileoverview User directory row.
 * @description One user's row in the User Management table: the avatar, name (a real button, so
 * keyboard users can open the user too) and Employee ID, email, role, department, reporting
 * manager, State, phone, date of joining, status, and the three-dot actions menu. Clicking the row
 * opens View User.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import {
  DIRECTORY_STATUS_LABEL_KEY,
  DIRECTORY_STATUS_VARIANT,
  formatJoiningDate,
  formatPhone,
  initialsFromName,
} from '@/features/users/utils/user-display.utils'
import { buildUserRowActions } from '@/features/users/utils/user-row-actions.utils'
import Badge from '@/shared/components/atoms/Badge'
import RowActionsMenu from '@/shared/components/molecules/RowActionsMenu'

import type { DirectoryUser } from '@/features/users/types/users.types'
import type { UserRowHandlers } from '@/features/users/utils/user-row-actions.utils'
import type { MouseEvent } from 'react'

interface UserDirectoryRowProps {
  user: DirectoryUser
  handlers: UserRowHandlers
  /** The table's grid template, so the row lines up with its header. */
  gridClassName: string
}

/**
 * @component
 * @param {UserDirectoryRowProps} props - The user, what their actions do, and the grid template.
 * @param {DirectoryUser} props.user - The row's user.
 * @param {UserRowHandlers} props.handlers - What the row and its menu's actions do.
 * @param {string} props.gridClassName - The table's grid template class.
 */
function UserDirectoryRow({ user, handlers, gridClassName }: UserDirectoryRowProps) {
  const { t } = useTranslation()

  /**
   * @description Opens the row's user in View User.
   */
  const openUser = () => {
    handlers.onView(user.userId)
  }

  /**
   * @description Opens the user from their name without also triggering the row.
   * @param {MouseEvent<HTMLButtonElement>} event - The click.
   */
  const openUserFromName = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    handlers.onView(user.userId)
  }

  return (
    <div
      data-testid="user-row"
      onClick={openUser}
      className={`grid ${gridClassName} cursor-pointer items-center gap-3 border-b border-row-border px-4 py-2.5 hover:bg-surface-hover sm:px-5`}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg bg-primary text-xs font-bold text-white">
          {initialsFromName(user.displayName)}
        </div>
        <div className="flex min-w-0 flex-col">
          <button
            type="button"
            onClick={openUserFromName}
            data-testid="user-name-button"
            className="min-w-0 cursor-pointer truncate text-left text-[13.5px] font-semibold text-ink hover:underline"
          >
            {user.displayName}
          </button>
          <span className="truncate text-xs text-footer" data-testid="user-employee-code">
            {user.employeeCode}
          </span>
        </div>
      </div>
      <span className="truncate text-[13px] text-cell" data-testid="user-email">
        {user.email}
      </span>
      <Badge
        data-testid="user-role-badge"
        variant={user.primaryPosition.code === 'SALES_EXECUTIVE' ? 'neutral' : 'info'}
      >
        {user.primaryPosition.name}
      </Badge>
      <span className="truncate text-[13px] text-cell" data-testid="user-department">
        {user.department?.name ?? '—'}
      </span>
      <span className="truncate text-[13px] text-cell" data-testid="user-reporting-manager">
        {user.reportingManager?.displayName ?? '—'}
      </span>
      <span className="truncate text-[13px] text-cell" data-testid="user-state">
        {user.state?.name ?? t('sales.userManagement.allIndia')}
      </span>
      <span className="text-[13px] text-cell" data-testid="user-phone">
        {formatPhone(user)}
      </span>
      {/* Set only once the invitation is accepted — blank while Pending Invitation. */}
      <span className="text-[13px] text-cell" data-testid="user-joining-date">
        {formatJoiningDate(user.dateOfJoining) || '—'}
      </span>
      <Badge
        variant={DIRECTORY_STATUS_VARIANT[user.directoryStatus]}
        dot
        data-testid="user-status-badge"
      >
        {t(DIRECTORY_STATUS_LABEL_KEY[user.directoryStatus])}
      </Badge>
      <RowActionsMenu
        label={t('sales.userManagement.rowActions', { name: user.displayName })}
        actions={buildUserRowActions(t, user, handlers)}
      />
    </div>
  )
}

export default UserDirectoryRow
